import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Approvals from "@/pages/Approvals";
import Assets from "@/pages/Assets";
import Vehicles from "@/pages/Vehicles";
import Facilities from "@/pages/Facilities";
import Maintenance from "@/pages/Maintenance";
import Documents from "@/pages/Documents";
import MasterData from "@/pages/MasterData";
import UsersPage from "@/pages/Users";
import AuditTrail from "@/pages/AuditTrail";
import Notifications from "@/pages/Notifications";
import Guide from "@/pages/Guide";
import Assignments from "@/pages/Assignments";
import Departures from "@/pages/Departures";
import Reports from "@/pages/Reports";
import MobilePreDeparture from "@/pages/MobilePreDeparture";
import Purchase from "@/pages/Purchase";
import Accounting from "@/pages/Accounting";
import RacOta from "@/pages/RacOta";
import RacPsychotest from "./pages/RacPsychotest";
import RacMeals from "./pages/RacMeals";
import { roleAllowed } from "@/lib/constants";

function Protected({ children, roles }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Memuat sesi...</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roleAllowed(user.role, roles)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/m/pre-departure" element={<Protected><MobilePreDeparture /></Protected>} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/tickets" element={<Tickets />} />
        <Route path="/tickets/:id" element={<TicketDetail />} />
        <Route path="/approvals" element={<Protected roles={["supervisor", "manager", "coo", "ceo", "legal_admin"]}><Approvals /></Protected>} />
        <Route path="/facilities" element={<Facilities />} />
        <Route path="/assets" element={<Assets />} />
        <Route path="/vehicles" element={<Vehicles />} />
        <Route path="/maintenance" element={<Maintenance />} />
        <Route path="/maintenance/history" element={<Maintenance history />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/master" element={<Protected roles={["ga_staff", "asset_pic", "legal_admin", "rac_staff"]}><MasterData /></Protected>} />
        <Route path="/users" element={<Protected roles={[]}><UsersPage /></Protected>} />
        <Route path="/audit" element={<Protected roles={["supervisor", "legal_admin"]}><AuditTrail /></Protected>} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/departures" element={<Departures />} />
        <Route path="/reports" element={<Protected roles={["supervisor", "ga_staff", "asset_pic", "legal_admin"]}><Reports /></Protected>} />
        <Route path="/purchase" element={<Protected roles={["supervisor", "ga_staff", "asset_pic", "accounting", "manager", "coo", "ceo"]}><Purchase /></Protected>} />
        <Route path="/accounting" element={<Protected roles={["supervisor", "ga_staff", "asset_pic", "accounting", "manager", "coo", "ceo"]}><Accounting /></Protected>} />
        <Route path="/rac/ota" element={<RacOta />} />
        <Route path="/rac/psychotest" element={<RacPsychotest />} />
        <Route path="/rac/meals" element={<RacMeals />} />
        <Route path="/assignments" element={<Protected roles={["supervisor", "ga_staff", "asset_pic", "legal_admin"]}><Assignments /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
